import { useState, useMemo, useRef, useEffect } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils.js';

function MultiChip({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const count = selected.length;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors',
          'min-h-[32px]',
          count > 0
            ? 'border-accent/40 bg-accent-soft text-accent'
            : 'border-border text-text-muted hover:border-border-strong hover:text-text'
        )}
      >
        <span className="font-medium">{label}</span>
        {count > 0 && (
          <span className="rounded-full bg-accent/20 px-1.5 text-[10px] font-semibold">
            {count}
          </span>
        )}
        <ChevronDown size={12} strokeWidth={2} className={cn('transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-48 rounded-lg border border-border bg-surface p-1 shadow-lg">
          {options.map((opt) => {
            const active = selected.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  onChange(active ? selected.filter((s) => s !== opt) : [...selected, opt]);
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-xs',
                  'min-h-[32px]',
                  active ? 'bg-accent-soft text-accent' : 'text-text hover:bg-border/30'
                )}
              >
                <span
                  className={cn(
                    'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border',
                    active ? 'border-accent bg-accent text-white' : 'border-border'
                  )}
                >
                  {active && <span className="text-[8px]">✓</span>}
                </span>
                <span className="truncate">{opt}</span>
              </button>
            );
          })}
          {options.length === 0 && (
            <p className="px-2.5 py-1.5 text-xs italic text-text-dim">None available</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function CCFilterBar({ sections, filters, onFiltersChange }) {
  const { stages, carriers } = useMemo(() => {
    const stageSet = new Set();
    const carrierSet = new Set();
    for (const s of sections || []) {
      for (const item of s.items || []) {
        if (item.stage) stageSet.add(item.stage);
        for (const c of item.carriers || []) {
          if (c.carrier_name) carrierSet.add(c.carrier_name);
        }
      }
    }
    return {
      stages: Array.from(stageSet).sort(),
      carriers: Array.from(carrierSet).sort(),
    };
  }, [sections]);

  const activeCount = (filters.stages?.length || 0) + (filters.carriers?.length || 0);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <MultiChip
        label="Stage"
        options={stages}
        selected={filters.stages || []}
        onChange={(v) => onFiltersChange({ ...filters, stages: v })}
      />
      <MultiChip
        label="Carrier"
        options={carriers}
        selected={filters.carriers || []}
        onChange={(v) => onFiltersChange({ ...filters, carriers: v })}
      />
      {activeCount > 0 && (
        <button
          type="button"
          onClick={() => onFiltersChange({ stages: [], carriers: [] })}
          className="inline-flex items-center gap-1 text-[10px] text-text-dim hover:text-text"
        >
          <X size={10} strokeWidth={2} />
          Clear
        </button>
      )}
    </div>
  );
}
